import React, { memo, useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Field, QueryBuilder, RuleGroupType } from "react-querybuilder";
import "react-querybuilder/dist/query-builder.css";
import { useOverlay } from "modules/providers/overlay-provider";
import QueryValueSelector from "modules/query-builder/query-value-selector";
import colors from "app/colors";
import SyncoTooltip from "modules/_shared/synco-tooltip";
import { HelpIcon } from "modules/icons";
import QueryAddRuleAction from "modules/query-builder/query-add-rule-action";
import QueryCombinatorSelector from "modules/query-builder/query-combinator-selector";
import QueryFieldSelector from "modules/query-builder/query-field-selector";
import QueryRemoveRuleAction from "modules/query-builder/query-remove-rule-action";
import QueryOperatorSelector from "modules/query-builder/query-operator-selector";
import { QUERY_OPERATORS, QUERY_COMBINATORS } from "modules/utils/Constants";
import useQueryString from "modules/hooks/use-query-string";
import useInternalAdminUserAttributeCategoryList from "../hooks/use-internal-admin-user-attribute-category-list";
import useInternalAdminUserAttributeList from "../hooks/use-internal-admin-user-attribute-list";

interface IProps {
  query?: RuleGroupType;
  onSave: Function;
}

const InternalAdminDistributionGroupQueryModal = ({
  query,
  onSave,
}: IProps) => {
  const { closeModal } = useOverlay();
  const { qsOrganizationId } = useQueryString();

  const { userAttributeCategories } = useInternalAdminUserAttributeCategoryList(
    {
      organizationId: qsOrganizationId,
      input: {
        limit: 1000000,
        offset: 0,
      },
    },
  );

  const { userAttributes } = useInternalAdminUserAttributeList({
    organizationId: qsOrganizationId,
    input: {
      limit: 1000000,
      offset: 0,
    },
  });

  const [error, setError] = useState<string>();

  const EMPTY_QUERY = {
    combinator: "and",
    rules: [
      {
        field: "",
        operator: "in",
        value: "",
      },
    ],
  };

  const [newQuery, setNewQuery] = useState<RuleGroupType | undefined>(
    query ?? EMPTY_QUERY,
  );

  const isValid = useMemo(() => {
    setError(undefined);
    if (!newQuery) return true;
    if (newQuery.rules.length === 0) return true;
    return newQuery.rules.every(
      (rule) =>
        typeof rule === "object" &&
        "field" in rule &&
        "value" in rule &&
        rule.field?.toString().trim() !== "" &&
        rule.value?.toString().trim() !== "",
    );
  }, [newQuery]);

  const onSaveQuery = useCallback(() => {
    if (!isValid) {
      setError("At least one rule is invalid");
      return;
    }
    if (!newQuery) return;
    if (newQuery.rules.length === 0) {
      onSave(null);
      closeModal();
      return;
    }
    onSave(newQuery);
    closeModal();
  }, [isValid, newQuery, onSave, closeModal]);

  const fields: Field[] = useMemo(
    () =>
      userAttributeCategories.map(
        (userAttributeCategory): Field => ({
          name: userAttributeCategory.id,
          label: userAttributeCategory.name,
          valueEditorType: "multiselect",
          operators: QUERY_OPERATORS,
          values: userAttributes
            .filter(
              (userAttribute) =>
                userAttribute.userAttributeCategoryId ===
                userAttributeCategory.id,
            )
            .map((userAttribute) => ({
              label: userAttribute.name,
              value: userAttribute.id,
              active: userAttribute.active,
            })),
        }),
      ),
    [userAttributeCategories, userAttributes],
  );

  return (
    <Modal isOpen onClose={closeModal} size="4xl" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={16}>
        <ModalBody>
          <Stack w="full" spacing={4} p={6} alignItems="center">
            <VStack w="full">
              <Text
                fontFamily="Outfit"
                fontWeight={700}
                fontSize={24}
                color={colors.blackSecondary}
              >
                Dynamic Group Membership Rules
              </Text>
              <Text fontWeight={500} fontSize={14} color={colors.gray50}>
                Combine user attributes to define dynamic membership for this
                group.
                <SyncoTooltip
                  label={
                    <Text>
                      Create rules based on users’ attribute categories (e.g.
                      “Title”) and values (e.g. “Property Manager”). Select{" "}
                      <Text as="span" fontWeight={700}>
                        “ALL RULES”
                      </Text>{" "}
                      - where group members must match all rules or select{" "}
                      <Text as="span" fontWeight={700}>
                        ANY RULE”
                      </Text>{" "}
                      - where group members can match any rule in the set.
                      Preview the resulting group member list in the{" "}
                      <Text as="span" fontWeight={700}>
                        Create/Edit Group
                      </Text>{" "}
                      panel.
                    </Text>
                  }
                  wrapInBox={false}
                >
                  <Text as="span" color={colors.blue}>
                    {" "}
                    <HelpIcon fill={colors.blue} /> Learn about Rules
                  </Text>
                </SyncoTooltip>
              </Text>
            </VStack>
            <Box w="full">
              <QueryBuilder
                fields={fields}
                query={newQuery}
                combinators={QUERY_COMBINATORS}
                operators={QUERY_OPERATORS}
                controlElements={{
                  valueEditor: QueryValueSelector,
                  addRuleAction: QueryAddRuleAction,
                  removeRuleAction: QueryRemoveRuleAction,
                  combinatorSelector: QueryCombinatorSelector,
                  fieldSelector: QueryFieldSelector,
                  operatorSelector: QueryOperatorSelector,
                  addGroupAction: () => null,
                  cloneGroupAction: () => null,
                  removeGroupAction: () => null,
                }}
                onQueryChange={setNewQuery}
              />
            </Box>
            {error && (
              <Text w="full" color={colors.red} textAlign="center">
                {error}
              </Text>
            )}
            <HStack w="full" spacing={4} justifyContent="center">
              <Button
                fontWeight={700}
                fontSize={16}
                variant="cancel"
                height={50}
                w={140}
                onClick={closeModal}
              >
                Cancel
              </Button>
              <Button
                fontWeight={700}
                fontSize={16}
                height={50}
                w={140}
                onClick={onSaveQuery}
              >
                Save Rules
              </Button>
            </HStack>
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(InternalAdminDistributionGroupQueryModal);
